import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../(others)/layout";

const META_DESCRIPTION =
  "Politique de confidentialité de l’application Capsules historiques : localisation, notifications, services tiers et données utilisées.";

const PagePolitiqueConfidentialiteCapsules = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    const previousDescription = description?.content;

    document.title =
      "Politique de confidentialité – Capsules historiques | Ahuncycle";
    description?.setAttribute("content", META_DESCRIPTION);

    return () => {
      document.title = previousTitle;
      if (description && previousDescription !== undefined) {
        description.setAttribute("content", previousDescription);
      }
    };
  }, []);

  return (
    <main className="py-8 sm:py-12 lg:py-16">
      <Layout>
        <article className="mx-auto max-w-4xl px-1 py-3 text-neutral-800 dark:text-neutral-200 sm:px-5 sm:py-6">
          <header className="border-b border-neutral-200 pb-7 dark:border-neutral-700">
            <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-green-700 dark:text-green-400">
              Ahuncycle
            </p>
            <h1 className="text-3xl font-semibold leading-tight text-neutral-900 dark:text-white sm:text-4xl lg:text-5xl">
              Politique de confidentialité – Capsules historiques
            </h1>
            <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-400">
              Dernière mise à jour : 14 juillet 2026
            </p>
          </header>

          <div className="space-y-9 pt-8 text-base leading-7 sm:text-lg">
            <p>
              L’application « Capsules historiques », publiée par Ahuncycle,
              permet de découvrir des bâtiments, monuments, œuvres d’art et
              lieux historiques à proximité grâce à la géolocalisation.
            </p>

            <section aria-labelledby="localisation">
              <h2 id="localisation" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                1. Localisation
              </h2>
              <p>
                L’application peut demander l’accès à votre localisation
                approximative ou précise pour :
              </p>
              <ul className="mt-3 list-disc space-y-1 pl-6">
                <li>déterminer les capsules situées à proximité;</li>
                <li>calculer la distance entre vous et les lieux;</li>
                <li>afficher votre position sur une carte;</li>
                <li>proposer un itinéraire;</li>
                <li>déclencher des notifications de proximité.</li>
              </ul>
              <p className="mt-3">
                La présente politique ne prétend pas que votre position reste
                exclusivement sur votre appareil. L’utilisation éventuelle de
                la localisation en arrière-plan doit être confirmée selon la
                version de l’application installée et les autorisations qu’elle
                demande.
              </p>
            </section>

            <section aria-labelledby="notifications">
              <h2 id="notifications" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                2. Notifications
              </h2>
              <p>
                L’application peut envoyer une notification lorsque vous
                approchez d’un lieu historique. Vous pouvez désactiver les
                notifications dans les paramètres de l’application, lorsque
                cette option est offerte, ou dans les réglages Android.
              </p>
            </section>

            <section aria-labelledby="backend">
              <h2 id="backend" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                3. Données et backend Ahuncycle
              </h2>
              <p>
                Le backend Ahuncycle sert principalement à fournir le catalogue
                des capsules, les textes historiques, les images et les fichiers
                audio. Lors de ces téléchargements, les requêtes réseau peuvent
                comporter des données techniques nécessaires à leur traitement,
                comme l’adresse IP, la date et l’heure de la requête, ainsi que
                des renseignements sur l’appareil ou le logiciel utilisé.
              </p>
            </section>

            <section aria-labelledby="services-tiers">
              <h2 id="services-tiers" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                4. Services tiers
              </h2>
              <p>
                Les services en ligne Ahuncycle sont hébergés au moyen de
                Vercel. Ce fournisseur peut traiter les données techniques des
                requêtes réseau nécessaires à la livraison du contenu. Aucun
                autre service tiers propre à l’application mobile n’est nommé
                ici sans confirmation de son utilisation réelle.
              </p>
            </section>

            <section aria-labelledby="cache">
              <h2 id="cache" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                5. Contenu téléchargé et cache
              </h2>
              <p>
                L’application télécharge des textes, des images et des fichiers
                audio relatifs aux capsules historiques. Certains contenus
                peuvent être conservés temporairement sur votre appareil afin
                d’améliorer les performances et de réduire les téléchargements.
              </p>
            </section>

            <section aria-labelledby="stockage-local">
              <h2 id="stockage-local" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                6. Stockage local
              </h2>
              <p>
                Selon les fonctions utilisées, l’application peut conserver
                localement vos préférences et paramètres, le catalogue mis en
                cache, des informations techniques liées aux notifications et
                l’état des autorisations accordées. Ces données peuvent
                généralement être supprimées en effaçant les données de
                l’application ou en la désinstallant.
              </p>
            </section>

            <section aria-labelledby="comptes">
              <h2 id="comptes" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                7. Comptes utilisateurs
              </h2>
              <p>L’application ne demande pas la création d’un compte utilisateur.</p>
            </section>

            <section aria-labelledby="publicite">
              <h2 id="publicite" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                8. Publicité
              </h2>
              <p>L’application ne diffuse aucune publicité.</p>
            </section>

            <section aria-labelledby="vente">
              <h2 id="vente" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                9. Vente de données
              </h2>
              <p>Ahuncycle ne vend pas les données personnelles des utilisateurs.</p>
            </section>

            <section aria-labelledby="enfants">
              <h2 id="enfants" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                10. Enfants
              </h2>
              <p>
                L’application est destinée au grand public et n’est pas conçue
                spécifiquement pour les enfants.
              </p>
            </section>

            <section aria-labelledby="securite">
              <h2 id="securite" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                11. Sécurité
              </h2>
              <p>
                Ahuncycle prend des mesures raisonnables pour protéger les
                données et limiter leur utilisation aux fonctions nécessaires.
                Aucune méthode de transmission ou de stockage ne peut toutefois
                garantir une sécurité absolue.
              </p>
            </section>

            <section aria-labelledby="choix">
              <h2 id="choix" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                12. Choix de l’utilisateur
              </h2>
              <p>Vous pouvez :</p>
              <ul className="mt-3 list-disc space-y-1 pl-6">
                <li>refuser ou retirer l’autorisation de localisation;</li>
                <li>désactiver les notifications;</li>
                <li>effacer les données de l’application;</li>
                <li>désinstaller l’application.</li>
              </ul>
            </section>

            <section aria-labelledby="modifications">
              <h2 id="modifications" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                13. Modifications de la politique
              </h2>
              <p>
                Cette politique peut être mise à jour pour refléter l’évolution
                de l’application ou de nos pratiques. La date affichée en haut
                de cette page sera modifiée en conséquence.
              </p>
            </section>

            <section aria-labelledby="contact">
              <h2 id="contact" className="mb-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                14. Contact
              </h2>
              <p>
                Pour toute question concernant cette politique, écrivez à{" "}
                <a className="font-medium text-green-700 underline hover:text-green-800 dark:text-green-400" href="mailto:ahuncycle@gmail.com">
                  ahuncycle@gmail.com
                </a>
                . Vous pouvez aussi visiter{" "}
                <a className="font-medium text-green-700 underline hover:text-green-800 dark:text-green-400" href="https://www.ahuncycle.com">
                  le site Ahuncycle
                </a>
                .
              </p>
            </section>

            <p className="border-t border-neutral-200 pt-7 dark:border-neutral-700">
              <Link className="font-semibold text-green-700 underline hover:text-green-800 dark:text-green-400" to="/">
                Retour au site Ahuncycle
              </Link>
            </p>
          </div>
        </article>
      </Layout>
    </main>
  );
};

export default PagePolitiqueConfidentialiteCapsules;
